/**
 * Typed content model for /bitrate-calculator.php
 * (Magento source of truth: https://www.getscw.com/bitrate-calculator.php).
 *
 * Source artifacts: output/page-rebuild/bitrate-calculator-php/source-content.html
 * + source-content-manifest.json + computed-styles-{desktop,mobile}.json.
 *
 * Each block id is stable for design-studio editing.
 * Source text, links, image src/alt, and form field names preserved exactly
 * from the live Magento page. The first source <h2> ("Security Camera
 * Bitrate / Bandwidth Calculator") is promoted to the route's single <h1>
 * per the typed-rebuild rule; an off-screen <h2> mirror keeps source-vs-
 * rendered heading manifests aligned.
 *
 * INTENTIONAL DIFFERENCE — HubSpot promo block dropped per user direction
 * (matches the established `/cctv-record-time-calculator.php` precedent).
 * Source has a "Want 10% off your next order? / Drop us your email!" block
 * loading `//js.hsforms.net/forms/v2.js` with portalId "5417380" /
 * formId "8245206e-a122-465c-905f-8eb7802cbd32". Resulting verify-content
 * deltas: 2 missing H3s ("Want 10% off your next order?", "Drop us your
 * email!"), 1 missing form (the HubSpot embed), and 2 missing source
 * script interactions (hsforms.net script + hbspt.forms.create call).
 */

export const slug = 'bitrate-calculator.php';
export const artifactSlug = 'bitrate-calculator-php';

export interface PageMetadata {
  title: string;
  metaTitle: string;
  description: string;
  facebookDomainVerification: string;
}

export const metadata: PageMetadata = {
  title: 'How to Calculate Security Camera Video Bitrate | SCW',
  metaTitle: 'How to Calculate Security Camera Video Bitrate | SCW',
  description:
    "How much data will watching or recording on your security cameras use? Security Camera Warehouse' bitrate calculator will help you determine that easily!",
  facebookDomainVerification: 'vppqp2ub6z1ukz74prkl3s1nu4zzcj',
};

export interface BreadcrumbItem {
  label: string;
  href?: string;
  title?: string;
}

export interface BreadcrumbsContent {
  blockId: 'bitrate-calculator.breadcrumbs';
  items: BreadcrumbItem[];
}

export const breadcrumbs: BreadcrumbsContent = {
  blockId: 'bitrate-calculator.breadcrumbs',
  items: [
    { label: 'Home', href: '/', title: 'Go to Home Page' },
    { label: 'Knowledge Base', href: '/knowledge-base', title: 'Knowledge Base' },
    { label: 'Security Camera Bitrate Calculator' },
  ],
};

export interface PrimaryCta {
  label: string;
  href: string;
}

export interface HeroContent {
  blockId: 'bitrate-calculator.hero';
  eyebrow: string;
  /** Promoted to <h1> per typed-rebuild rule (source <h2> in section-title). */
  title: string;
  body: string;
  primaryCta: PrimaryCta;
  /** Production hero background (S3 hosted). */
  backgroundImage: string;
}

export const hero: HeroContent = {
  blockId: 'bitrate-calculator.hero',
  eyebrow: 'Tool:',
  title: 'Security Camera Bitrate / Bandwidth Calculator',
  body: 'Use this calculator to determine how much data watching or recording your cameras will use.',
  primaryCta: {
    label: 'Get a Quote',
    href: '/get-a-quote',
  },
  backgroundImage:
    'https://scw-commerce-media.s3.amazonaws.com/pub/media/images/jumbotrons/product/Scope/IMG_0778.jpg',
};

export interface PartnerAsideContent {
  blockId: 'bitrate-calculator.partnerAside';
  /** Plain "If you're in the security industry, " prefix. */
  prefix: string;
  link: { label: string; href: string };
  image: { src: string; sourceSrc: string; alt: string; width: number; height: number };
}

export const partnerAside: PartnerAsideContent = {
  blockId: 'bitrate-calculator.partnerAside',
  prefix: "If you're in the security industry, ",
  link: { label: 'check out our partner program.', href: '/installer-accounts' },
  image: {
    src: 'https://scw-commerce-media.s3.amazonaws.com/pub/media/images/calculator/installer.png',
    sourceSrc: '/images/calculator/installer.png',
    alt: '',
    width: 360,
    height: 360,
  },
};

export interface ResolutionOption {
  label: string;
  /** kbps value used by the source calcDrive() formula. */
  bitrate: number;
}

export interface EncoderOption {
  label: string;
  /** Encoder multiplier (.5 = H265, 1 = H264). */
  multiplier: number;
  /** Whether option is selected by default in source. */
  selected?: boolean;
}

export interface CalculatorContent {
  blockId: 'bitrate-calculator.calculator';
  formName: 'cctvcalc';
  /** Visible-but-empty span content rendered before user interaction. */
  promptHeading: string;
  cameraImage: { src: string; sourceSrc: string; alt: string };
  resolutionImage: { src: string; sourceSrc: string; alt: string };
  encodingImage: { src: string; sourceSrc: string; alt: string };
  resolutionOptions: ResolutionOption[];
  encoderOptions: EncoderOption[];
  /** Hidden warning text rendered (display:none) per source. */
  warningText: string;
}

const calculatorImageBase = 'https://scw-commerce-media.s3.amazonaws.com/pub/media/images/calculator';

export const calculator: CalculatorContent = {
  blockId: 'bitrate-calculator.calculator',
  formName: 'cctvcalc',
  promptHeading: 'Fill out the calculator, find out your storage needs:',
  cameraImage: {
    src: `${calculatorImageBase}/camera.png`,
    sourceSrc: '/images/calculator/camera.png',
    alt: '',
  },
  resolutionImage: {
    src: `${calculatorImageBase}/resolution.png`,
    sourceSrc: '/images/calculator/resolution.png',
    alt: '',
  },
  encodingImage: {
    src: `${calculatorImageBase}/encoding.png`,
    sourceSrc: '/images/calculator/encoding.png',
    alt: '',
  },
  /**
   * Source resolution options preserved verbatim — labels and bitrate values
   * (kbps) drive the calcDrive() formula. Lowercase "4k" preserved from
   * source HTML.
   */
  resolutionOptions: [
    { label: 'Substream (720 x 576)', bitrate: 1024 },
    { label: '720P (1280 x 720)', bitrate: 2048 },
    { label: '1080P (1920 x 1080)', bitrate: 4096 },
    { label: '3MP (2048 x 1536)', bitrate: 5000 },
    { label: '4MP (2688 x 1520)', bitrate: 6144 },
    { label: '4k (3840 x 2160)', bitrate: 8192 },
    { label: '12MP (4000 x 3000)', bitrate: 12288 },
  ],
  /** Trailing space after labels preserved (source <option>H264 </option>). */
  encoderOptions: [
    { label: 'H264 ', multiplier: 1 },
    { label: 'H265 ', multiplier: 0.5, selected: true },
  ],
  warningText: 'Please enter a number',
};

export interface BodyParagraphContent {
  blockId: 'bitrate-calculator.bodyIntro';
  text: string;
}

export const bodyIntro: BodyParagraphContent = {
  blockId: 'bitrate-calculator.bodyIntro',
  text: 'Input the total number of cameras you will want to be able to view remotely and the calculator will determine the amount of data being sent. Remember, all cameras come with a secondary "Substream" used for streaming the cameras at a lower quality while retaining full record quality.',
};

export interface RelatedGuide {
  /** Lead text rendered inside <p class="lead">: "Related Guide: <link>". */
  leadPrefix: string;
  link: { label: string; href: string };
  body: string;
}

export interface RelatedGuidesContent {
  blockId: 'bitrate-calculator.relatedGuides';
  guides: RelatedGuide[];
}

export const relatedGuides: RelatedGuidesContent = {
  blockId: 'bitrate-calculator.relatedGuides',
  guides: [
    {
      leadPrefix: 'Related Guide: ',
      link: {
        label: 'What Internet Speed Do I Need for Remote Viewing Security Cameras?',
        href: '/support/faq/internet-speed-camera-viewing',
      },
      body: "Want more information about what internet upload speed you need to watch your security cameras remotely? Here's a guide about our minimum recommendations and with information on how to test your network connection speed at both your sending and receiving locations.",
    },
    {
      leadPrefix: 'Related Guide: ',
      link: {
        label: 'IP Camera Bitrate and Compression',
        href: '/knowledge-base/camera-bitrate-compression',
      },
      body: "Not all 1080P cameras are created equal. Here's a guide with more information about what happens if a camera's processor has a slow bitrate.",
    },
  ],
};

export interface H265ArticleContent {
  blockId: 'bitrate-calculator.h265Article';
  heading: string;
  image: {
    src: string;
    sourceSrc: string;
    alt: string;
    width: number;
    height: number;
  };
  paragraphs: string[];
}

export const h265Article: H265ArticleContent = {
  blockId: 'bitrate-calculator.h265Article',
  heading: 'Stuck on the H.265 Option?',
  image: {
    src: 'https://scw-commerce-media.s3.amazonaws.com/images/features/h265.jpg',
    sourceSrc: '/images/features/h265.jpg',
    alt: 'video compression',
    width: 280,
    height: 210,
  },
  paragraphs: [
    "Our 2018 line (and a few other company's 2018 lines) records with a new more efficient file H.265 compression, so that you can stream video about at almost half the normal bitrate with the about the same quality recording. H.265 replaces H.264. H.26X is block-oriented motion-compensation-based video compression. In plain english, H.26X, rather than updating the whole frame at every refresh, splits the screen into horizontal and vertical grids and only updates the grids with changes. H.265 increases the flexibility of these grid containers so that surveillance video stream uses less bitrate than H.264. (The new line is backwards compatible with H.264.)",
    "If you don't know what encoder version you have, use H.264 as that is more common.",
  ],
};

export interface SubstreamArticleContent {
  blockId: 'bitrate-calculator.substreamArticle';
  heading: string;
  paragraphs: string[];
}

export const substreamArticle: SubstreamArticleContent = {
  blockId: 'bitrate-calculator.substreamArticle',
  heading: 'Use H.265 and Substream Viewing To Lower Upload Requirements',
  paragraphs: [
    'When viewing cameras with H.265 and substream you dramatically lower the bitrate and therefore the upload speed requirements of your network.',
  ],
};
